import { useState } from 'react';

import { PropTypes } from 'prop-types';

import { FormattedMessage, FormattedNumber, useIntl } from '@zenbusiness/application-commons-intl';
import AccordionDetails from '@zenbusiness/zui/AccordionDetails';
import AccordionSummary from '@zenbusiness/zui/AccordionSummary';
import Box from '@zenbusiness/zui/Box';
import Chip from '@zenbusiness/zui/Chip';
import Stack from '@zenbusiness/zui/Stack';

import {
  InvoiceCardRoot,
  InvoiceCardAccordion,
  InvoiceCardHeader,
  InvoiceCardTitleRow,
  InvoiceCardAvatar,
  InvoiceCardAmountColumn,
  InvoiceCardTitle,
  InvoiceCardDueDate,
  InvoiceCardAmount,
  InvoiceCardDetailsSurface,
  InvoiceCardDetailsContent,
  InvoiceCardDivider,
  InvoiceCardLockIcon,
  InvoiceCardLockedMessage,
  InvoiceCardActions,
  InvoiceCardPrimaryButton,
  InvoiceCardKlarnaLogo,
  InvoiceCardSecondaryButton
} from './InvoiceCard.style';
import { KLARNA_LOGO_URL } from '../../constants';

const MESSAGE_IDS = {
  title: 'dashboard.klarnaLanding.invoiceCard.title',
  dueDate: 'dashboard.klarnaLanding.invoiceCard.dueDate',
  details: 'dashboard.klarnaLanding.invoiceCard.details',
  unpaid: 'dashboard.klarnaLanding.invoiceCard.unpaid',
  payWithKlarna: 'dashboard.klarnaLanding.invoiceCard.payWithKlarna',
  payWithAnotherMethod: 'dashboard.klarnaLanding.invoiceCard.payWithAnotherMethod',
  payPreviousFirst: 'dashboard.klarnaLanding.invoiceCard.payPreviousFirst',
  klarnaLogoAlt: 'dashboard.klarnaLanding.invoiceCard.klarnaLogoAlt'
};

function InvoiceCard({
  amount,
  avatarUrl,
  dueDate,
  isLocked = false,
  onPayWithKlarna,
  onPayWithAnotherMethod
}) {
  const { formatMessage: fm } = useIntl();
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  return (
    <InvoiceCardRoot isLocked={isLocked}>
      <InvoiceCardHeader>
        <InvoiceCardTitleRow>
          <Box>
            <InvoiceCardTitle>
              <FormattedMessage id={MESSAGE_IDS.title} />
            </InvoiceCardTitle>
            <InvoiceCardDueDate>
              <FormattedMessage id={MESSAGE_IDS.dueDate} values={{ dueDate }} />
            </InvoiceCardDueDate>
          </Box>
          {!isLocked && avatarUrl && <InvoiceCardAvatar src={avatarUrl} />}
        </InvoiceCardTitleRow>
        <InvoiceCardAmountColumn>
          <InvoiceCardAmount>
            {/* eslint-disable-next-line react/style-prop-object */}
            <FormattedNumber currency="USD" style="currency" value={amount} />
          </InvoiceCardAmount>
          <Chip color="error" label={<FormattedMessage id={MESSAGE_IDS.unpaid} />} size="sm" />
        </InvoiceCardAmountColumn>
      </InvoiceCardHeader>

      <InvoiceCardAccordion
        clickstreamProps={{ elementName: 'klarna_invoice_details_accordion' }}
        disableGutters
        expanded={isLocked ? false : detailsExpanded}
        isLocked={isLocked}
        onChange={(event, expanded) => {
          if (!isLocked) {
            setDetailsExpanded(expanded);
          }
        }}
      >
        <AccordionSummary aria-disabled={isLocked}>
          <FormattedMessage id={MESSAGE_IDS.details} />
        </AccordionSummary>
        <AccordionDetails>
          <InvoiceCardDetailsSurface>
            <InvoiceCardDetailsContent>
              {/* Placeholder for invoice detail content — wired in a future ticket */}
            </InvoiceCardDetailsContent>
          </InvoiceCardDetailsSurface>
        </AccordionDetails>
      </InvoiceCardAccordion>

      {isLocked ? (
        <>
          <InvoiceCardDivider />
          <InvoiceCardLockedMessage>
            <InvoiceCardLockIcon aria-hidden />
            <FormattedMessage id={MESSAGE_IDS.payPreviousFirst} />
          </InvoiceCardLockedMessage>
        </>
      ) : (
        <InvoiceCardActions>
          <InvoiceCardPrimaryButton
            clickstreamProps={{ elementName: 'klarna_pay_with_klarna' }}
            onClick={onPayWithKlarna}
          >
            <Stack alignItems="center" direction="row" justifyContent="center" spacing={1}>
              <FormattedMessage id={MESSAGE_IDS.payWithKlarna} />
              <InvoiceCardKlarnaLogo
                alt={fm({ id: MESSAGE_IDS.klarnaLogoAlt })}
                src={KLARNA_LOGO_URL}
              />
            </Stack>
          </InvoiceCardPrimaryButton>
          <InvoiceCardSecondaryButton
            clickstreamProps={{ elementName: 'klarna_pay_other_method' }}
            onClick={onPayWithAnotherMethod}
          >
            <FormattedMessage id={MESSAGE_IDS.payWithAnotherMethod} />
          </InvoiceCardSecondaryButton>
        </InvoiceCardActions>
      )}
    </InvoiceCardRoot>
  );
}

InvoiceCard.propTypes = {
  amount: PropTypes.number.isRequired,
  avatarUrl: PropTypes.string,
  dueDate: PropTypes.string.isRequired,
  isLocked: PropTypes.bool,
  onPayWithKlarna: PropTypes.func,
  onPayWithAnotherMethod: PropTypes.func
};

export default InvoiceCard;
